import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  getTrueBundleConfig,
  isTrueBundleFeature,
  isTrueBundleOnSale,
  PlanInterval,
} from '../../../../models';
import { fromSubscription } from '../../../../store';
import { getUnitPrice } from '../../../../hook';
import { useLaunchDarkly } from '../../../../launchdarkly';
import { TRUE_BUNDLE_STARTER_OPTIONS, TrueBundlePlan } from './trueBundlePlans';

/**
 * The True Bundle Starters this account may buy right now. Non-empty = the page switches to the
 * "Choose your plan" layout (Starter banner + card grid); empty = the regular "Add seats to your
 * plan" table, unchanged.
 *
 * Every condition is fail-closed - a Starter appears only when ALL of them are known to hold: the
 * package is configured in LD `trueBundleConfig` and still on sale, the BE says this is a first
 * purchase (`is-access-starter-package` -> `isNewAccount === true`, never `undefined`), no bundle
 * is owned yet (any source), and the package's eCom subtotal (`subscriptionPlanPrice.<plan>
 * Annually` - NOT the struck-through list price) is seeded. Without that key the card would
 * advertise "$0".
 */
export const useStarterBundlePlans = (accountId: number): TrueBundlePlan[] => {
  const { plans } = useStarterLayoutState(accountId);
  return plans;
};

/**
 * `useStarterBundlePlans` plus a `pending` signal: the LD side says a Starter COULD be offered
 * (configured, on sale, price seeded, no bundle owned), but the `is-access-starter-package` call
 * has not resolved, so which layout to draw is not known yet. Callers render a skeleton while
 * `pending` instead of flashing the regular table and swapping it for the Starter cards a beat
 * later. A failed call resolves with `isNewAccount === undefined`, so `pending` always clears and
 * the page falls back to the regular table (fail-closed, unchanged).
 */
export const useStarterLayoutState = (
  accountId: number,
): { plans: TrueBundlePlan[]; pending: boolean } => {
  const { flags } = useLaunchDarkly();
  const selectAccountSubscriptions = useMemo(
    () => fromSubscription.selectByAccountId(accountId),
    [accountId],
  );
  const accountSubscriptions = useSelector(selectAccountSubscriptions);
  const isNewAccount = useSelector(fromSubscription.selectIsNewAccount);
  const newAccountStatus = useSelector(fromSubscription.selectNewAccountStatus);
  const hasBundleSubscription = useMemo(
    () => (accountSubscriptions ?? []).some(sub => isTrueBundleFeature(sub.feature)),
    [accountSubscriptions],
  );

  return useMemo(
    () => {
      const configuredPlans = TRUE_BUNDLE_STARTER_OPTIONS.filter(plan => {
        const config = getTrueBundleConfig(flags?.trueBundleConfig, plan.feature);
        const ecomSubtotal = getUnitPrice(
          flags?.subscriptionPlanPrice,
          plan.feature,
          PlanInterval.YEAR,
          'product',
        );
        return isTrueBundleOnSale(config) && ecomSubtotal > 0;
      });
      const offerPossible = configuredPlans.length > 0 && !hasBundleSubscription;
      return {
        plans: offerPossible && isNewAccount === true ? configuredPlans : [],
        pending: offerPossible && newAccountStatus !== 'resolved',
      };
    },
    [
      flags?.subscriptionPlanPrice,
      flags?.trueBundleConfig,
      hasBundleSubscription,
      isNewAccount,
      newAccountStatus,
    ],
  );
};
